(() => {
  const namespace = "GuruSearch";
  if (window[namespace]) return;

  const rootSelector = "[data-guru-search-root]";
  const desktopQuery = window.matchMedia("(min-width: 1024px)");
  const navigationRequests = new WeakMap();
  const previewRequests = new WeakMap();
  const previewTimers = new WeakMap();
  const previewGenerations = new WeakMap();
  const validatedPreviews = new WeakMap();
  const queryTimers = new WeakMap();
  const intentGenerations = new WeakMap();
  const suggestionRequests = new WeakMap();
  const failedNavigations = new WeakMap();
  const infiniteLoadingStates = new WeakMap();
  const initializedRoots = new WeakSet();
  const loadingOwners = new Set();
  const transportDeadlineMs = 8000;
  const bodyDeadlineMs = 5000;

  class SearchDeadlineError extends Error {
    constructor(stage) {
      super(`Search ${stage} timed out`);
      this.name = "SearchDeadlineError";
    }
  }

  class SearchPageRangeError extends Error {
    constructor() {
      super("Search page is outside the authoritative result range");
      this.name = "SearchPageRangeError";
    }
  }

  const fallbackTranslations = Object.freeze({
    product: "product",
    products: "products",
    filters: "Filters",
    hideFilters: "Hide filters",
    showCount: "Show __COUNT__ __NOUN__",
    zeroMatches: "No products match",
    updating: "Updating …",
    previewFailed: "Could not check the result",
    didYouMean: "Did you mean __SUGGESTION__?",
    routeError: "The search results could not be updated.",
    updatingProducts: "Updating products …",
    countShown: "__COUNT__ __NOUN__ shown.",
    updateFailed: "Could not update the products.",
    previewFailedRetry: "The filter selection could not be checked. Try again.",
    loadMore: "Load more products",
    loadingMore: "Loading more products …",
    loadedMore: "__COUNT__ more loaded.",
    loadMoreRetry: "Try to load more products",
    loadMoreFailed: "More products could not be loaded automatically.",
  });

  const catalogControls = window.GuruCatalogControls?.create({
    rootSelector,
    surface: "search",
    i18nSelector: "[data-guru-search-i18n]",
    fallbackTranslations,
  });
  if (!catalogControls) return;
  const {
    dialogFor,
    formFor,
    isNonNegativeSafeInteger,
    isPositiveSafeInteger,
    localizedCopy,
    matchingFacetInput,
    normalizeText,
    parseSection,
    productNoun,
    resetFacetEvidence,
    rootFor,
    sectionFor,
    setBusy,
    syncQuickFilters,
  } = catalogControls;
  const normalizedPathname = (pathname) => pathname === "/" ? pathname : pathname.replace(/\/+$/, "");
  const localeFor = (root) => normalizeText(root?.dataset.guruSearchLocale || document.documentElement.lang || "en");
  const relativeUrl = (url) => `${url.pathname}${url.search}`;

  const abortError = () => new DOMException("Search request was superseded", "AbortError");
  const settleBefore = (factory, controller, timeout, stage) => new Promise((resolve, reject) => {
    let settled = false;
    const signal = controller?.signal;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      callback(value);
    };
    const onAbort = () => finish(reject, abortError());
    const timer = window.setTimeout(() => {
      const error = new SearchDeadlineError(stage);
      finish(reject, error);
      controller?.abort();
    }, timeout);

    if (signal?.aborted) {
      finish(reject, abortError());
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    Promise.resolve()
      .then(factory)
      .then((value) => finish(resolve, value), (error) => finish(reject, error));
  });

  const fetchSearchText = async (url, options, controller) => {
    const response = await settleBefore(
      () => fetch(url, { ...options, signal: controller.signal }),
      controller,
      transportDeadlineMs,
      "fetch",
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return settleBefore(() => response.text(), controller, bodyDeadlineMs, "body");
  };

  const routeUrl = (root) => {
    const route = new URL(root?.dataset.guruSearchRoute || "", window.location.href);
    if (
      route.origin !== window.location.origin ||
      route.username ||
      route.password ||
      route.hash ||
      !normalizedPathname(route.pathname).endsWith("/search")
    ) {
      throw new Error("Search route is not authoritative");
    }
    route.search = "";
    return route;
  };

  const sortOptionsFor = (root) => {
    const nodes = root?.querySelectorAll(":scope > script[data-guru-search-sort-options]") || [];
    if (nodes.length !== 1) throw new Error("Search sort options must be unique");
    const values = JSON.parse(nodes[0].textContent || "[]");
    if (
      !Array.isArray(values) ||
      values.length > 32 ||
      values.some((value) => typeof value !== "string" || !value || value.length > 80) ||
      new Set(values).size !== values.length
    ) {
      throw new Error("Search sort options are invalid");
    }
    return new Set(values);
  };

  const validateFilterName = (name) => (
    /^filter\.(?:p|v|t)\.[^\s=&]{1,150}$/.test(name)
  );

  const canonicalSearchUrl = (root, value) => {
    const source = new URL(value, window.location.href);
    const route = routeUrl(root);
    if (
      source.origin !== route.origin ||
      source.username ||
      source.password ||
      source.hash ||
      normalizedPathname(source.pathname) !== normalizedPathname(route.pathname)
    ) {
      throw new Error("Search URL does not match the authoritative route");
    }

    const output = new URL(route);
    const queryValues = source.searchParams.getAll("q");
    if (queryValues.length > 1) throw new Error("Search URL has multiple query values");
    const query = normalizeText(queryValues[0] || "");
    if (query.length > 80) throw new Error("Search query is too long");
    if (query) {
      output.searchParams.set("q", query);
      output.searchParams.set("type", "product");
    }

    const sortValues = source.searchParams.getAll("sort_by");
    if (sortValues.length > 1) throw new Error("Search URL has multiple sort values");
    const sort = normalizeText(sortValues[0] || "");
    const allowedSorts = sortOptionsFor(root);
    if (sort && allowedSorts.has(sort) && query) output.searchParams.set("sort_by", sort);

    source.searchParams.forEach((filterValue, filterName) => {
      if (!validateFilterName(filterName) || !query) return;
      const valueText = `${filterValue}`.trim();
      if (!valueText || valueText.length > 500) {
        throw new Error("Search URL has an invalid filter value");
      }
      if (filterName === "filter.v.price.gte" || filterName === "filter.v.price.lte") {
        const numeric = Number(valueText);
        if (!Number.isFinite(numeric) || numeric < 0) {
          throw new Error("Search URL has an invalid price filter");
        }
        output.searchParams.append(filterName, `${numeric}`);
      } else {
        output.searchParams.append(filterName, valueText);
      }
    });

    const pageValues = source.searchParams.getAll("page");
    if (pageValues.length > 1) throw new Error("Search URL has multiple page values");
    if (pageValues.length === 1 && query) {
      const page = Number(pageValues[0]);
      if (!isPositiveSafeInteger(page)) throw new Error("Search URL has an invalid page value");
      if (page > 1) output.searchParams.set("page", `${page}`);
    }

    return output;
  };

  const sortedStateEntries = (url, defaultSort = "", omitPage = false) => {
    const state = new URLSearchParams(url.search);
    state.delete("section_id");
    if (omitPage) state.delete("page");
    if (state.get("q") && !state.has("sort_by") && defaultSort) {
      state.set("sort_by", defaultSort);
    }
    return Array.from(state.entries())
      .sort(([leftName, leftValue], [rightName, rightValue]) => (
        leftName.localeCompare(rightName) || leftValue.localeCompare(rightValue)
      ))
      .map(([name, value]) => `${name}\u0000${value}`);
  };

  const paramsFromForm = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, name) => {
      const text = `${value}`.trim();
      if (text) params.append(name, text);
    });
    return params;
  };

  const urlFromFilterForm = (root, form) => {
    const url = routeUrl(root);
    url.search = paramsFromForm(form).toString();
    url.searchParams.delete("page");
    return canonicalSearchUrl(root, url);
  };

  const boundedProductFallbackUrl = (root, value = window.location.href, preferredQuery = null) => {
    let route;
    try {
      route = routeUrl(root);
    } catch (_error) {
      route = new URL("/search", window.location.origin);
    }

    let source = null;
    try {
      source = new URL(value, window.location.href);
    } catch (_error) {
      source = null;
    }
    const explicitQuery = preferredQuery === null ? "" : normalizeText(preferredQuery);
    const rootQuery = normalizeText(root?.dataset.guruSearchQuery || "");
    const sourceQuery = normalizeText(source?.searchParams?.get("q") || "");
    const query = (preferredQuery === null ? rootQuery || sourceQuery : explicitQuery).slice(0, 80);
    route.search = "";
    if (query) {
      route.searchParams.set("q", query);
      route.searchParams.set("type", "product");
    }
    return route;
  };

  const urlFromQuery = (root, query) => {
    let url;
    try {
      url = canonicalSearchUrl(root, window.location.href);
    } catch (_error) {
      url = boundedProductFallbackUrl(root, window.location.href, query);
    }
    const normalized = normalizeText(query).slice(0, 80);
    url.searchParams.delete("page");
    if (normalized) {
      url.searchParams.set("q", normalized);
      url.searchParams.set("type", "product");
    } else {
      url.search = "";
    }
    try {
      return canonicalSearchUrl(root, url);
    } catch (_error) {
      return boundedProductFallbackUrl(root, window.location.href, normalized);
    }
  };

  const stateUrlFromRoot = (root) => {
    const form = root?.querySelector("[data-guru-search-state-form]");
    if (!(form instanceof HTMLFormElement)) throw new Error("Search state form is missing");
    const url = routeUrl(root);
    url.search = paramsFromForm(form).toString();
    return canonicalSearchUrl(root, url);
  };

  const headMetadataFromRoot = (root, requestedValue) => {
    const requestedUrl = canonicalSearchUrl(root, requestedValue);
    const nodes = root?.querySelectorAll(":scope > script[data-guru-search-head]") || [];
    if (nodes.length !== 1) throw new Error("Search head data must be unique");
    const metadata = JSON.parse(nodes[0].textContent || "{}");
    const title = normalizeText(metadata.title || "");
    const query = normalizeText(metadata.query || "");
    const page = Number(metadata.page);
    if (
      Object.keys(metadata).sort().join("\u0000") !== "page\u0000query\u0000title" ||
      !title ||
      title.length > 240 ||
      /[\u0000-\u001f\u007f]/.test(title) ||
      query !== (requestedUrl.searchParams.get("q") || "") ||
      !isPositiveSafeInteger(page) ||
      page !== Number(requestedUrl.searchParams.get("page") || 1)
    ) {
      throw new Error("Search head data is invalid");
    }
    const canonical = stateUrlFromRoot(root);
    const defaultSort = root.dataset.guruSearchDefaultSort || "";
    if (
      sortedStateEntries(canonical, defaultSort).join("\u0001") !==
      sortedStateEntries(requestedUrl, defaultSort).join("\u0001")
    ) {
      throw new Error("Search canonical does not match the authoritative state");
    }
    return { title, canonical: requestedUrl };
  };

  const canonicalLinkForDocument = () => {
    const links = document.querySelectorAll('link[rel~="canonical"]');
    if (links.length !== 1 || !(links[0] instanceof HTMLLinkElement)) {
      throw new Error("Search canonical link must be unique");
    }
    return links[0];
  };

  const applyHeadMetadata = (metadata) => {
    const canonicalLink = canonicalLinkForDocument();
    document.title = metadata.title;
    canonicalLink.href = metadata.canonical.href;
  };

  const setPageLiveMessage = (root, message) => {
    const live = root?.querySelector("[data-guru-search-live]");
    if (live) live.textContent = message;
  };

  const setDialogLiveMessage = (root, message) => {
    const live = root?.querySelector("[data-guru-search-dialog-live]");
    if (live) live.textContent = message;
  };

  const setNavigationError = (root, message = "", retryable = false) => {
    const error = root?.querySelector("[data-guru-search-error]");
    if (error) {
      error.hidden = !message;
      const copy = error.querySelector("[data-guru-search-error-copy]");
      const retry = error.querySelector("[data-guru-search-retry]");
      if (copy) copy.textContent = message;
      if (retry) retry.hidden = !message || !retryable;
    }
  };

  const setDialogError = (root, message = "") => {
    const dialogError = root?.querySelector("[data-guru-search-dialog-error]");
    if (dialogError) {
      dialogError.hidden = !message;
      dialogError.textContent = message;
    }
  };

  const claimLoading = (owner) => {
    if (!owner || loadingOwners.has(owner)) return;
    const wasIdle = loadingOwners.size === 0;
    loadingOwners.add(owner);
    if (wasIdle) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    }
  };

  const releaseLoading = (owner) => {
    if (!loadingOwners.delete(owner) || loadingOwners.size !== 0) return;
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  };

  const beginIntent = (root, { abortNavigation = true } = {}) => {
    if (!root) return 0;
    cleanupInfiniteLoading(root);
    window.clearTimeout(queryTimers.get(root));
    queryTimers.delete(root);
    if (abortNavigation) navigationRequests.get(root)?.abort();
    suggestionRequests.get(root)?.abort();
    cancelPreview(root);
    if (dialogFor(root)?.open) markDraftPending(root);
    const generation = (intentGenerations.get(root) || 0) + 1;
    intentGenerations.set(root, generation);
    return generation;
  };

  const intentIsCurrent = (root, generation) => (
    root?.isConnected && intentGenerations.get(root) === generation
  );

  const focusDescriptor = (target) => {
    if (!(target instanceof Element)) return null;
    if (target.matches("[data-guru-search-input]")) return { kind: "query" };
    if (target.matches("[data-guru-search-sort]")) return { kind: "sort" };
    if (target.matches("[data-guru-search-filter-reset]")) return { kind: "reset" };
    const quick = target.closest("[data-guru-search-quick-filter]");
    if (quick) return { kind: "quick", type: quick.dataset.guruSearchQuickFilter || "" };
    if (target.matches("[data-guru-panel-quick-control]")) {
      return { kind: "panel-quick", type: target.dataset.guruPanelQuickControl || "" };
    }
    if (target instanceof HTMLInputElement && target.closest("[data-guru-search-filter-form]")) {
      return { kind: "filter", name: target.name, value: target.value, inputType: target.type };
    }
    return null;
  };

  const restoreFocus = (root, descriptor, fallback = "") => {
    if (!root || (!descriptor && !fallback)) return;
    let target = null;
    if (descriptor?.kind === "query") {
      target = root.querySelector("[data-guru-search-input]");
    } else if (descriptor?.kind === "sort") {
      target = root.querySelector("[data-guru-search-sort]");
    } else if (descriptor?.kind === "reset") {
      target = root.querySelector("[data-guru-search-filter-reset]");
    } else if (descriptor?.kind === "quick") {
      target = Array.from(root.querySelectorAll("[data-guru-search-quick-filter]"))
        .find((candidate) => candidate.dataset.guruSearchQuickFilter === descriptor.type && candidate.getClientRects().length);
    } else if (descriptor?.kind === "panel-quick") {
      target = Array.from(root.querySelectorAll("[data-guru-panel-quick-control]"))
        .find((candidate) => candidate.dataset.guruPanelQuickControl === descriptor.type);
    } else if (descriptor?.kind === "filter") {
      target = Array.from(formFor(root)?.elements || []).find((candidate) => (
        candidate instanceof HTMLInputElement &&
        candidate.name === descriptor.name &&
        candidate.type === descriptor.inputType &&
        candidate.value === descriptor.value
      ));
    } else if (descriptor?.kind === "grid") {
      target = root.querySelector("[data-guru-search-grid]");
    }

    if (!(target instanceof HTMLElement) || target.disabled || !target.getClientRects().length) {
      if (fallback === "query") target = root.querySelector("[data-guru-search-input]");
      else if (fallback === "sort") target = root.querySelector("[data-guru-search-sort]");
      else target = root.querySelector("[data-guru-search-filter-trigger]");
    }
    if (target instanceof HTMLElement && !target.disabled) target.focus({ preventScroll: true });
  };

  const syncSortValue = (root) => {
    const select = root?.querySelector("[data-guru-search-sort]");
    const value = root?.querySelector("[data-guru-search-sort-value]");
    if (select && value) value.textContent = select.selectedOptions[0]?.textContent?.trim() || "";
  };

  const applyFor = (root) => root?.querySelector("[data-guru-search-filter-apply]");

  const setApplyState = (root, { disabled, copy }) => {
    const apply = applyFor(root);
    if (!apply) return;
    apply.disabled = Boolean(disabled);
    apply.textContent = copy;
  };

  const validatedFilterUrl = (root, form, { report = false } = {}) => {
    if (!(form instanceof HTMLFormElement)) return null;
    if (!form.checkValidity()) {
      if (report) form.reportValidity();
      return null;
    }
    try {
      return urlFromFilterForm(root, form);
    } catch (_error) {
      return null;
    }
  };

  const markDraftPending = (root) => {
    const form = formFor(root);
    if (form) validatedPreviews.delete(form);
    setApplyState(root, { disabled: true, copy: localizedCopy(root, "updating") });
    setDialogError(root);
    setDialogLiveMessage(root, localizedCopy(root, "updating"));
  };

  const markDraftInvalid = (root, { report = false } = {}) => {
    const form = formFor(root);
    if (form) {
      validatedPreviews.delete(form);
      if (report) form.reportValidity();
    }
    setApplyState(root, { disabled: true, copy: localizedCopy(root, "previewFailed") });
    setDialogError(root, localizedCopy(root, "previewFailedRetry"));
    setDialogLiveMessage(root, "");
  };

  const resetApplyToServerState = (root) => {
    const form = formFor(root);
    const total = Number(root?.dataset.guruSearchTotal);
    if (!form || !isNonNegativeSafeInteger(total)) return;
    const destination = validatedFilterUrl(root, form);
    const generation = previewGenerations.get(form) || 0;
    if (total > 0 && destination) {
      validatedPreviews.set(form, { generation, url: relativeUrl(destination), total });
    } else {
      validatedPreviews.delete(form);
    }
    setApplyState(root, {
      disabled: total === 0 || !destination,
      copy: total === 0
        ? localizedCopy(root, "zeroMatches")
        : localizedCopy(root, "showCount", { count: total, noun: productNoun(root, total) }),
    });
  };

  const resetDraftEvidence = (root) => {
    const form = formFor(root);
    if (!form) return;
    resetFacetEvidence(form);
    syncQuickFilters(root);
    resetApplyToServerState(root);
  };

  const cancelPreview = (root, { resetApply = false } = {}) => {
    const form = formFor(root);
    if (!form) return;
    previewGenerations.set(form, (previewGenerations.get(form) || 0) + 1);
    window.clearTimeout(previewTimers.get(form));
    previewTimers.delete(form);
    previewRequests.get(form)?.abort();
    previewRequests.delete(form);
    validatedPreviews.delete(form);
    if (resetApply) {
      resetApplyToServerState(root);
    }
  };

  const closeFilters = (root, { restore = true, discardDraft = true } = {}) => {
    const dialog = dialogFor(root);
    if (!dialog) return;
    cancelPreview(root, { resetApply: discardDraft });
    if (dialog.open) dialog.close();
    root.querySelector("[data-guru-search-layout]")?.classList.remove("is-filter-visible");
    document.body.classList.remove("guru-search-filter-open");
    const trigger = root.querySelector("[data-guru-search-filter-trigger]");
    trigger?.setAttribute("aria-expanded", "false");
    const label = trigger?.querySelector("[data-guru-search-filter-label]");
    if (label) label.textContent = localizedCopy(root, "filters");
    setDialogError(root);
    setDialogLiveMessage(root, "");
    if (discardDraft) {
      formFor(root)?.reset();
      resetDraftEvidence(root);
    }
    if (restore) trigger?.focus({ preventScroll: true });
  };

  const openFilters = (root) => {
    const dialog = dialogFor(root);
    const trigger = root?.querySelector("[data-guru-search-filter-trigger]");
    if (!root || !dialog || dialog.open) return;
    if (desktopQuery.matches && root.dataset.guruSearchFilterLayout === "sidebar") {
      dialog.show();
      root.querySelector("[data-guru-search-layout]")?.classList.add("is-filter-visible");
      const label = trigger?.querySelector("[data-guru-search-filter-label]");
      if (label) label.textContent = localizedCopy(root, "hideFilters");
    } else {
      dialog.showModal();
      document.body.classList.add("guru-search-filter-open");
      requestAnimationFrame(() => root.querySelector("[data-guru-search-filter-close]")?.focus());
    }
    trigger?.setAttribute("aria-expanded", "true");
  };

  const validateIncomingSection = (root, incomingSection, requestedValue) => {
    const requestedUrl = canonicalSearchUrl(root, requestedValue);
    const expectedSectionId = root.dataset.guruSearchSectionId || "";
    const incomingRoots = incomingSection?.querySelectorAll(rootSelector) || [];
    const incomingRoot = incomingRoots.length === 1 ? incomingRoots[0] : null;
    if (
      !expectedSectionId ||
      incomingSection?.id !== `shopify-section-${expectedSectionId}` ||
      !incomingSection.classList.contains("shopify-section--main-search") ||
      !incomingRoot ||
      incomingRoot.dataset.guruSearchSectionId !== expectedSectionId
    ) {
      throw new Error("Search section response has an invalid identity");
    }

    const currentRoute = routeUrl(root);
    const incomingRoute = routeUrl(incomingRoot);
    if (normalizedPathname(currentRoute.pathname) !== normalizedPathname(incomingRoute.pathname)) {
      throw new Error("Search section response has an invalid route");
    }

    const query = requestedUrl.searchParams.get("q") || "";
    const incomingQuery = normalizeText(incomingRoot.dataset.guruSearchQuery || "");
    const performed = incomingRoot.dataset.guruSearchPerformed === "true";
    const productOnly = incomingRoot.dataset.guruSearchProductOnly === "true";
    const normalizing = incomingRoot.dataset.guruSearchNormalize === "true";
    if (
      incomingQuery !== query ||
      performed !== Boolean(query) ||
      (query && (!productOnly || normalizing)) ||
      (!query && productOnly)
    ) {
      throw new Error("Search section response does not match product-only search");
    }

    const pageSize = Number(incomingRoot.dataset.guruSearchPageSize);
    const total = Number(incomingRoot.dataset.guruSearchTotal);
    const pages = Number(incomingRoot.dataset.guruSearchPages);
    const currentPage = Number(incomingRoot.dataset.guruSearchCurrentPage);
    const renderedCount = Number(incomingRoot.dataset.guruSearchRenderedCount);
    const requestedPage = Number(requestedUrl.searchParams.get("page") || 1);
    const expectedPageSize = Number(root.dataset.guruSearchPageSize);
    if (
      !isPositiveSafeInteger(pageSize) ||
      pageSize !== expectedPageSize ||
      !isNonNegativeSafeInteger(total) ||
      !isPositiveSafeInteger(pages) ||
      !isPositiveSafeInteger(currentPage) ||
      !isNonNegativeSafeInteger(renderedCount)
    ) {
      throw new Error("Search section response has invalid counters");
    }

    const expectedPages = Math.max(1, Math.ceil(total / pageSize));
    if (pages !== expectedPages) {
      throw new Error("Search section response has invalid pagination");
    }
    if (requestedPage > expectedPages) {
      throw new SearchPageRangeError();
    }
    if (currentPage !== requestedPage) {
      throw new Error("Search section response has an unexpected current page");
    }
    const expectedRendered = query
      ? Math.max(0, Math.min(pageSize, total - ((currentPage - 1) * pageSize)))
      : 0;
    const grid = incomingRoot.querySelector("[data-guru-search-grid]");
    const gridChildren = grid ? Array.from(grid.children) : [];
    const cards = gridChildren.filter((child) => child.matches("[data-guru-product-card]"));
    const emptyStates = gridChildren.filter((child) => child.matches(".guru-search__empty"));
    const hasExactGridState = expectedRendered > 0
      ? gridChildren.length === cards.length && emptyStates.length === 0
      : gridChildren.length === 1 && cards.length === 0 && emptyStates.length === 1;
    if (
      !grid ||
      currentPage > expectedPages ||
      renderedCount !== expectedRendered ||
      cards.length !== expectedRendered ||
      !hasExactGridState ||
      cards.some((card) => !card.matches("product-card.product-card.guru-product-card[data-guru-product-card]"))
    ) {
      throw new Error("Search section response has an inconsistent product grid");
    }

    const rootState = stateUrlFromRoot(incomingRoot);
    const defaultSort = incomingRoot.dataset.guruSearchDefaultSort || "";
    if (
      sortedStateEntries(requestedUrl, defaultSort).join("\u0001") !==
      sortedStateEntries(rootState, defaultSort).join("\u0001")
    ) {
      throw new Error("Search section response does not match requested URL state");
    }

    const countCopy = incomingRoot.querySelector("[data-guru-search-result-count]")?.textContent?.trim();
    const nounCopy = incomingRoot.querySelector("[data-guru-search-result-noun]")?.textContent?.trim();
    if (query && (countCopy !== `${total}` || nounCopy !== productNoun(incomingRoot, total))) {
      throw new Error("Search section response has an inconsistent result count");
    }

    const emptyState = emptyStates[0] || null;
    if (
      (!query && !emptyState?.querySelector(".guru-search__empty-links")) ||
      (query && total === 0 && (
        !emptyState?.matches("[data-guru-search-empty]") ||
        normalizeText(emptyState.querySelector("[data-guru-search-empty-query]")?.textContent || "") !== query ||
        !emptyState.querySelector("[data-guru-search-spelling]") ||
        !emptyState.querySelector(".guru-search__empty-links")
      ))
    ) {
      throw new Error("Search section response has an inconsistent empty state");
    }

    const filterForms = incomingRoot.querySelectorAll("[data-guru-search-filter-form]");
    const filterDialogs = incomingRoot.querySelectorAll("[data-guru-search-filter-dialog]");
    const filterTriggers = incomingRoot.querySelectorAll("[data-guru-search-filter-trigger]");
    const sortControls = incomingRoot.querySelectorAll("[data-guru-search-sort]");
    const incomingHasFilters = incomingRoot.dataset.guruSearchHasFilters === "true";
    const incomingHasSort = incomingRoot.dataset.guruSearchHasSort === "true";
    if (
      !["true", "false"].includes(incomingRoot.dataset.guruSearchHasFilters) ||
      !["true", "false"].includes(incomingRoot.dataset.guruSearchHasSort) ||
      filterForms.length !== (incomingHasFilters ? 1 : 0) ||
      filterDialogs.length !== (incomingHasFilters ? 1 : 0) ||
      filterTriggers.length !== (incomingHasFilters ? 1 : 0) ||
      sortControls.length !== (incomingHasSort ? 1 : 0)
    ) {
      throw new Error("Search section response has inconsistent controls");
    }

    const incomingFilterForm = filterForms[0] || null;
    if (incomingFilterForm) {
      const action = new URL(incomingFilterForm.action, window.location.href);
      if (
        action.origin !== currentRoute.origin ||
        normalizedPathname(action.pathname) !== normalizedPathname(currentRoute.pathname)
      ) {
        throw new Error("Search section response has an invalid filter form");
      }
    }

    const head = headMetadataFromRoot(incomingRoot, requestedUrl);
    return { incomingRoot, incomingFilterForm, total, head };
  };

  const productCardIdentity = (card) => card?.dataset?.guruProductId?.trim() || "";

  const validatedInfiniteCards = (grid, cards) => {
    const currentIds = new Set(Array.from(grid.querySelectorAll(":scope > [data-guru-product-card]"))
      .map(productCardIdentity)
      .filter(Boolean));
    const nextCards = Array.from(cards || []);
    const nextIds = new Set();
    const hasInvalidCard = nextCards.some((card) => {
      const identity = productCardIdentity(card);
      if (!card.matches?.("[data-guru-product-card]") || !identity || currentIds.has(identity) || nextIds.has(identity)) {
        return true;
      }
      nextIds.add(identity);
      return false;
    });
    if (nextCards.length === 0 || hasInvalidCard) {
      throw new Error("Next search page contains invalid or duplicate product cards");
    }
    return nextCards;
  };

  const cleanupInfiniteLoading = (root) => {
    const state = infiniteLoadingStates.get(root);
    state?.observer?.disconnect();
    state?.controller?.abort();
    releaseLoading(state?.controller);
    state?.sentinel?.remove();
    infiniteLoadingStates.delete(root);
    root?.removeAttribute("data-guru-catalog-infinite");
    setBusy(root, false);
  };

  const loadNextSearchPage = async (root) => {
    const state = infiniteLoadingStates.get(root);
    if (!state || state.loading || !root.isConnected) return false;
    state.loading = true;
    state.observer.disconnect();
    state.fallback.hidden = true;
    state.loadingCopy.hidden = false;
    const controller = new AbortController();
    state.controller = controller;
    setBusy(root, true);
    claimLoading(controller);
    setPageLiveMessage(root, localizedCopy(root, "loadingMore"));

    try {
      const nextUrl = canonicalSearchUrl(root, state.nextUrl);
      const sectionId = root.dataset.guruSearchSectionId || "";
      if (!sectionId) throw new Error("Search section id is missing");
      const requestUrl = new URL(nextUrl);
      requestUrl.searchParams.set("section_id", sectionId);
      const html = await fetchSearchText(requestUrl, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }, controller);
      if (infiniteLoadingStates.get(root) !== state || state.controller !== controller || !root.isConnected) {
        throw new DOMException("Stale search response", "AbortError");
      }
      const incomingSection = parseSection(html, sectionId);
      if (!incomingSection) throw new Error("Search section markup is missing");
      const validated = validateIncomingSection(root, incomingSection, nextUrl);
      const incomingGrid = validated.incomingRoot.querySelector("[data-guru-search-grid]");
      const incomingPagination = validated.incomingRoot.querySelector("[data-guru-search-pagination]");
      const grid = root.querySelector("[data-guru-search-grid]");
      const currentPagination = root.querySelector("[data-guru-search-pagination]");
      const marker = root.querySelector("[data-guru-search-current-page]");
      if (!incomingGrid || !incomingPagination || !grid || !currentPagination || !marker) {
        throw new Error("Search result area cannot be extended");
      }
      const cards = Array.from(incomingGrid.querySelectorAll(":scope > [data-guru-product-card]"))
        .map((card) => document.importNode(card, true));
      const nextCards = validatedInfiniteCards(grid, cards);
      const pagination = document.importNode(incomingPagination, true);
      grid.append(...nextCards);
      marker.dataset.guruSearchCurrentPage = `${nextUrl.searchParams.get("page") || 1}`;
      currentPagination.replaceWith(pagination);
      state.controller = null;
      releaseLoading(controller);
      setBusy(root, false);
      cleanupInfiniteLoading(root);
      setPageLiveMessage(root, localizedCopy(root, "loadedMore", { count: nextCards.length }));
      root.dispatchEvent(new CustomEvent("guru:search:page-appended", {
        bubbles: true,
        detail: { loaded: nextCards.length, page: Number(nextUrl.searchParams.get("page") || 1), url: nextUrl.href },
      }));
      enhanceInfiniteLoading(root);
      return true;
    } catch (error) {
      if (error?.name === "AbortError") return false;
      if (infiniteLoadingStates.get(root) !== state || state.controller !== controller) return false;
      state.controller = null;
      state.loading = false;
      state.loadingCopy.hidden = true;
      state.fallback.hidden = false;
      state.fallback.textContent = localizedCopy(root, "loadMoreRetry");
      setPageLiveMessage(root, localizedCopy(root, "loadMoreFailed"));
      setBusy(root, false);
      return false;
    } finally {
      releaseLoading(controller);
    }
  };

  const enhanceInfiniteLoading = (root) => {
    cleanupInfiniteLoading(root);
    const pagination = root?.querySelector("[data-guru-search-pagination]");
    if (!pagination || !("IntersectionObserver" in window)) return;
    const next = pagination.querySelector('a[rel="next"]');
    if (!(next instanceof HTMLAnchorElement)) {
      pagination.hidden = true;
      root.dataset.guruCatalogInfinite = "complete";
      return;
    }

    const sentinel = document.createElement("div");
    sentinel.className = "guru-catalog-controls__infinite";
    sentinel.dataset.guruSearchInfiniteSentinel = "";
    const loadingCopy = document.createElement("span");
    loadingCopy.className = "guru-catalog-controls__infinite-loading";
    loadingCopy.dataset.guruSearchInfiniteLoading = "";
    loadingCopy.hidden = true;
    loadingCopy.setAttribute("aria-hidden", "true");
    loadingCopy.textContent = localizedCopy(root, "loadingMore");
    const fallback = document.createElement("a");
    fallback.className = "guru-button guru-button--secondary guru-catalog-controls__infinite-fallback";
    fallback.dataset.guruSearchLoadMore = "";
    fallback.href = next.href;
    fallback.hidden = true;
    fallback.textContent = localizedCopy(root, "loadMore");
    sentinel.append(loadingCopy, fallback);
    pagination.append(sentinel);
    pagination.hidden = false;
    root.dataset.guruCatalogInfinite = "ready";

    const state = {
      controller: null,
      fallback,
      loading: false,
      loadingCopy,
      nextUrl: next.href,
      observer: null,
      sentinel,
    };
    state.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNextSearchPage(root);
    }, { rootMargin: "600px 0px" });
    infiniteLoadingStates.set(root, state);
    state.observer.observe(sentinel);
  };

  const initializeSuggestion = async (root) => {
    const button = root?.querySelector("[data-guru-search-spelling]");
    const query = normalizeText(root?.dataset.guruSearchQuery || "");
    if (!(button instanceof HTMLButtonElement) || !query) return;

    suggestionRequests.get(root)?.abort();
    const controller = new AbortController();
    suggestionRequests.set(root, controller);

    try {
      const predictive = new URL(root.dataset.guruSearchPredictiveRoute || "", window.location.href);
      if (
        predictive.origin !== window.location.origin ||
        predictive.username ||
        predictive.password ||
        predictive.hash ||
        !normalizedPathname(predictive.pathname).endsWith("/search/suggest")
      ) {
        throw new Error("Predictive search route is invalid");
      }
      predictive.search = "";
      predictive.searchParams.set("q", query);
      predictive.searchParams.set("resources[type]", "query");
      predictive.searchParams.set("resources[limit]", "3");
      predictive.searchParams.set("resources[limit_scope]", "each");
      predictive.searchParams.set("section_id", "predictive-search");

      const html = await fetchSearchText(predictive, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }, controller);
      if (suggestionRequests.get(root) !== controller || !root.isConnected) return;
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const fragments = parsed.querySelectorAll("[data-guru-predictive-search-response]");
      if (fragments.length !== 1) throw new Error("Predictive search response identity is missing");
      const fragment = fragments[0];
      const locale = localeFor(root);
      if (normalizeText(fragment.dataset.guruPredictiveSearchTerm || "").toLocaleLowerCase(locale) !== query.toLocaleLowerCase(locale)) {
        throw new Error("Predictive search response does not match the query");
      }
      const template = fragment.querySelector("template[data-guru-predictive-queries]");
      if (!(template instanceof HTMLTemplateElement)) throw new Error("Predictive search response query data is missing");
      const suggestion = Array.from(template.content.querySelectorAll("[data-guru-predictive-query]"))
        .map((node) => normalizeText(node.dataset.guruPredictiveQuery || ""))
        .find((value) => value && value.length <= 80 && value.toLocaleLowerCase(locale) !== query.toLocaleLowerCase(locale));
      if (!suggestion) return;
      const destination = urlFromQuery(root, suggestion);
      button.textContent = localizedCopy(root, "didYouMean", { suggestion });
      button.dataset.guruSearchSpellingUrl = relativeUrl(destination);
      button.hidden = false;
    } catch (error) {
      if (error?.name !== "AbortError") button.hidden = true;
    } finally {
      if (suggestionRequests.get(root) === controller) suggestionRequests.delete(root);
    }
  };

  const initializeRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    if (!intentGenerations.has(root)) intentGenerations.set(root, 0);
    syncQuickFilters(root);
    syncSortValue(root);
    resetDraftEvidence(root);

    let canonical;
    try {
      canonical = canonicalSearchUrl(root, window.location.href);
    } catch (_error) {
      canonical = boundedProductFallbackUrl(root, window.location.href);
    }
    const current = new URL(window.location.href);
    const needsNormalization = root.dataset.guruSearchNormalize === "true" ||
      relativeUrl(canonical) !== `${current.pathname}${current.search}`;
    if (needsNormalization) {
      replaceSearch(root, canonical, {
        historyMode: "replace",
        focusFallback: "query",
        retryable: false,
        fallbackAttempted: true,
      });
      return;
    }

    try {
      const validated = validateIncomingSection(root, sectionFor(root), canonical);
      applyHeadMetadata(validated.head);
    } catch (error) {
      if (error instanceof SearchPageRangeError && canonical.searchParams.has("page")) {
        canonical.searchParams.delete("page");
        replaceSearch(root, canonical, {
          historyMode: "replace",
          focusFallback: "query",
          retryable: false,
          pageRecoveryAttempted: true,
        });
      } else {
        let recovery = null;
        try {
          recovery = stateUrlFromRoot(root);
        } catch (_recoveryError) {
          recovery = null;
        }
        if (recovery && relativeUrl(recovery) !== `${current.pathname}${current.search}`) {
          replaceSearch(root, recovery, {
            historyMode: "replace",
            focusFallback: "query",
            retryable: false,
            fallbackAttempted: true,
          });
        } else {
          failedNavigations.delete(root);
          setNavigationError(root, localizedCopy(root, "routeError"), false);
        }
      }
      return;
    }
    initializeSuggestion(root);
    enhanceInfiniteLoading(root);
  };

  const replaceSearch = async (root, requestedValue, options = {}) => {
    const section = sectionFor(root);
    const sectionId = root?.dataset.guruSearchSectionId || section?.id?.replace("shopify-section-", "");
    if (!root || !section || !sectionId) return null;

    const intentGeneration = options.intentGeneration ?? beginIntent(root);
    if (!intentIsCurrent(root, intentGeneration)) return null;

    let requestedUrl;
    try {
      requestedUrl = canonicalSearchUrl(root, requestedValue);
    } catch (_error) {
      const fallback = boundedProductFallbackUrl(root, requestedValue);
      const current = new URL(window.location.href);
      if (!options.fallbackAttempted && relativeUrl(fallback) !== `${current.pathname}${current.search}`) {
        return replaceSearch(root, fallback, {
          ...options,
          historyMode: "replace",
          retryable: false,
          fallbackAttempted: true,
          intentGeneration,
        });
      }
      failedNavigations.delete(root);
      setNavigationError(root, localizedCopy(root, "routeError"), false);
      return null;
    }

    navigationRequests.get(root)?.abort();
    const controller = new AbortController();
    navigationRequests.set(root, controller);
    const isCurrent = () => (
      navigationRequests.get(root) === controller &&
      intentIsCurrent(root, intentGeneration)
    );
    setBusy(root, true);
    setNavigationError(root);
    setPageLiveMessage(root, localizedCopy(root, "updatingProducts"));
    claimLoading(controller);

    const requestUrl = new URL(requestedUrl);
    requestUrl.searchParams.set("section_id", sectionId);

    try {
      const html = await fetchSearchText(requestUrl, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }, controller);
      if (!isCurrent()) {
        throw new DOMException("Stale Search response", "AbortError");
      }
      const incomingSection = parseSection(html, sectionId);
      if (!incomingSection) throw new Error("Search section markup is missing");
      const validated = validateIncomingSection(root, incomingSection, requestedUrl);
      if (!isCurrent()) {
        throw new DOMException("Stale Search response", "AbortError");
      }

      const nextRoot = document.importNode(validated.incomingRoot, true);
      nextRoot.querySelectorAll("noscript").forEach((node) => node.remove());
      canonicalLinkForDocument();
      if (options.historyMode === "replace") {
        history.replaceState({ guruSearch: true }, "", relativeUrl(requestedUrl));
      } else if (options.historyMode !== "none") {
        history.pushState({ guruSearch: true }, "", relativeUrl(requestedUrl));
      }

      closeFilters(root, { restore: false, discardDraft: false });
      suggestionRequests.get(root)?.abort();
      root.replaceWith(nextRoot);
      applyHeadMetadata(validated.head);
      intentGenerations.set(nextRoot, intentGeneration);
      failedNavigations.delete(root);
      initializeRoot(nextRoot);
      if (options.keepDesktopFilters && desktopQuery.matches) openFilters(nextRoot);
      if (options.scrollToGrid) {
        nextRoot.querySelector("[data-guru-search-grid]")?.scrollIntoView({ block: "start", behavior: "auto" });
      }
      restoreFocus(nextRoot, options.focusTarget, options.focusFallback || "");
      setPageLiveMessage(nextRoot, localizedCopy(nextRoot, "countShown", {
        count: validated.total,
        noun: productNoun(nextRoot, validated.total),
      }));
      return nextRoot;
    } catch (error) {
      if (error?.name === "AbortError") return null;
      if (
        error instanceof SearchPageRangeError &&
        requestedUrl.searchParams.has("page") &&
        !options.pageRecoveryAttempted &&
        intentIsCurrent(root, intentGeneration)
      ) {
        requestedUrl.searchParams.delete("page");
        return replaceSearch(root, requestedUrl, {
          ...options,
          historyMode: "replace",
          retryable: false,
          pageRecoveryAttempted: true,
          intentGeneration,
        });
      }
      if (!isCurrent()) return null;
      const retryable = options.retryable !== false;
      if (retryable) {
        const retryOptions = { ...options };
        delete retryOptions.intentGeneration;
        delete retryOptions.pageRecoveryAttempted;
        delete retryOptions.fallbackAttempted;
        failedNavigations.set(root, { url: requestedUrl, options: retryOptions });
      } else {
        failedNavigations.delete(root);
      }
      setNavigationError(root, localizedCopy(root, "updateFailed"), retryable);
      setPageLiveMessage(root, "");
      return null;
    } finally {
      releaseLoading(controller);
      if (navigationRequests.get(root) === controller) {
        navigationRequests.delete(root);
        setBusy(root, false);
      }
    }
  };

  const syncDraftEvidence = (root, form, incomingForm) => {
    Array.from(form.elements).forEach((field) => {
      if (!(field instanceof HTMLInputElement) || field.type !== "checkbox" || !field.name) return;
      const candidate = matchingFacetInput(incomingForm, field);
      if (!candidate) {
        if (!field.checked) field.disabled = true;
        return;
      }
      field.disabled = candidate.disabled;
      const currentCount = field.closest("label")?.querySelector("[data-guru-facet-count]");
      const incomingCount = candidate.closest("label")?.querySelector("[data-guru-facet-count]");
      if (currentCount && incomingCount) currentCount.textContent = incomingCount.textContent;
    });
    form.querySelectorAll("[data-guru-panel-quick-control]").forEach((control) => {
      const type = control.dataset.guruPanelQuickControl;
      const incoming = incomingForm.querySelector(`[data-guru-panel-quick-control="${type}"]`);
      if (!incoming) {
        control.disabled = !control.checked;
        return;
      }
      control.disabled = incoming.disabled;
      const currentCount = control.closest("label")?.querySelector("[data-guru-facet-count]");
      const incomingCount = incoming.closest("label")?.querySelector("[data-guru-facet-count]");
      if (currentCount && incomingCount) currentCount.textContent = incomingCount.textContent;
    });
    syncQuickFilters(root);
  };

  const previewDraft = async (root, generation, intentGeneration) => {
    const form = formFor(root);
    const dialog = dialogFor(root);
    const apply = applyFor(root);
    const sectionId = root?.dataset.guruSearchSectionId;
    if (!form || !dialog?.open || !apply || !sectionId || previewGenerations.get(form) !== generation) return;

    const requestedUrl = validatedFilterUrl(root, form);
    if (!requestedUrl) {
      markDraftInvalid(root);
      return;
    }

    previewRequests.get(form)?.abort();
    const controller = new AbortController();
    previewRequests.set(form, controller);
    const isCurrent = () => (
      previewRequests.get(form) === controller &&
      previewGenerations.get(form) === generation &&
      intentGenerations.get(root) === intentGeneration &&
      root.isConnected &&
      dialog.open
    );

    markDraftPending(root);
    const requestUrl = new URL(requestedUrl);
    requestUrl.searchParams.set("section_id", sectionId);

    try {
      const html = await fetchSearchText(requestUrl, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }, controller);
      if (!isCurrent()) throw new DOMException("Stale filter preview", "AbortError");
      const incomingSection = parseSection(html, sectionId);
      const validated = validateIncomingSection(root, incomingSection, requestedUrl);
      if (!isCurrent()) throw new DOMException("Stale filter preview", "AbortError");
      if (validated.incomingFilterForm) syncDraftEvidence(root, form, validated.incomingFilterForm);
      if (validated.total > 0) {
        validatedPreviews.set(form, {
          generation,
          url: relativeUrl(requestedUrl),
          total: validated.total,
        });
      } else {
        validatedPreviews.delete(form);
      }
      const copy = validated.total === 0
        ? localizedCopy(root, "zeroMatches")
        : localizedCopy(root, "showCount", {
          count: validated.total,
          noun: productNoun(root, validated.total),
        });
      setApplyState(root, { disabled: validated.total === 0, copy });
      setDialogError(root);
      setDialogLiveMessage(root, copy);
    } catch (error) {
      if (error?.name === "AbortError" || !isCurrent()) return;
      markDraftInvalid(root);
    } finally {
      if (previewRequests.get(form) === controller) previewRequests.delete(form);
    }
  };

  const queueDraftPreview = (root) => {
    const form = formFor(root);
    if (!form) return;
    const intentGeneration = beginIntent(root);
    const generation = (previewGenerations.get(form) || 0) + 1;
    previewGenerations.set(form, generation);
    window.clearTimeout(previewTimers.get(form));
    previewRequests.get(form)?.abort();
    markDraftPending(root);
    if (!validatedFilterUrl(root, form)) {
      markDraftInvalid(root);
      return;
    }
    const timer = window.setTimeout(() => {
      if (
        previewGenerations.get(form) !== generation ||
        intentGenerations.get(root) !== intentGeneration
      ) return;
      previewTimers.delete(form);
      previewDraft(root, generation, intentGeneration);
    }, 180);
    previewTimers.set(form, timer);
  };

  const queueQuery = (root, input) => {
    const intentGeneration = beginIntent(root);
    const clear = root.querySelector("[data-guru-search-query-clear]");
    if (clear) clear.hidden = normalizeText(input.value).length === 0;
    const timer = window.setTimeout(() => {
      if (!intentIsCurrent(root, intentGeneration)) return;
      queryTimers.delete(root);
      replaceSearch(root, urlFromQuery(root, input.value), {
        historyMode: "push",
        focusTarget: { kind: "query" },
        focusFallback: "query",
        intentGeneration,
      });
    }, 360);
    queryTimers.set(root, timer);
  };

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = rootFor(target);
    if (!root) return;

    const trigger = target.closest("[data-guru-search-filter-trigger]");
    if (trigger) {
      event.preventDefault();
      if (desktopQuery.matches && dialogFor(root)?.open) closeFilters(root);
      else openFilters(root);
      return;
    }

    if (target.closest("[data-guru-search-filter-close]")) {
      event.preventDefault();
      closeFilters(root);
      return;
    }

    const reset = target.closest("[data-guru-search-filter-reset]");
    if (reset) {
      event.preventDefault();
      const form = formFor(root);
      form?.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
      form?.querySelectorAll('input[type="number"]').forEach((input) => { input.value = ""; });
      syncQuickFilters(root);
      if (desktopQuery.matches && form) {
        const destination = validatedFilterUrl(root, form, { report: true });
        if (!destination) {
          beginIntent(root);
          markDraftInvalid(root);
          return;
        }
        replaceSearch(root, destination, {
          historyMode: "push",
          keepDesktopFilters: true,
          focusTarget: focusDescriptor(reset),
          focusFallback: "filter",
        });
      } else {
        queueDraftPreview(root);
      }
      return;
    }

    const quick = target.closest("[data-guru-search-quick-filter]");
    if (quick) {
      event.preventDefault();
      if (quick.disabled) return;
      const type = quick.dataset.guruSearchQuickFilter;
      const inputs = Array.from(root.querySelectorAll(`[data-guru-${type}-input]`));
      const shouldSelect = !inputs.some((input) => input.checked);
      inputs.forEach((input) => {
        if (!input.disabled || input.checked) input.checked = shouldSelect;
      });
      syncQuickFilters(root);
      const form = formFor(root);
      const destination = validatedFilterUrl(root, form, { report: true });
      if (!destination) {
        beginIntent(root);
        markDraftInvalid(root);
        return;
      }
      replaceSearch(root, destination, {
        historyMode: "push",
        focusTarget: focusDescriptor(quick),
        focusFallback: "filter",
        keepDesktopFilters: dialogFor(root)?.open,
      });
      return;
    }

    const clearQuery = target.closest("[data-guru-search-query-clear]");
    if (clearQuery) {
      event.preventDefault();
      const input = root.querySelector("[data-guru-search-input]");
      if (!input) return;
      window.clearTimeout(queryTimers.get(root));
      queryTimers.delete(root);
      input.value = "";
      clearQuery.hidden = true;
      replaceSearch(root, urlFromQuery(root, ""), {
        historyMode: "push",
        focusTarget: { kind: "query" },
        focusFallback: "query",
      });
      return;
    }

    const spelling = target.closest("[data-guru-search-spelling]");
    if (spelling?.dataset.guruSearchSpellingUrl) {
      event.preventDefault();
      replaceSearch(root, spelling.dataset.guruSearchSpellingUrl, {
        historyMode: "push",
        focusFallback: "query",
      });
      return;
    }

    const loadMore = target.closest("[data-guru-search-load-more]");
    if (loadMore) {
      event.preventDefault();
      loadNextSearchPage(root);
      return;
    }

    const link = target.closest("[data-guru-search-link], [data-guru-search-pagination] a");
    if (link instanceof HTMLAnchorElement) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === "_blank") return;
      event.preventDefault();
      replaceSearch(root, link.href, {
        historyMode: "push",
        scrollToGrid: Boolean(link.closest("[data-guru-search-pagination]")),
        focusTarget: link.closest("[data-guru-search-pagination]") ? { kind: "grid" } : null,
        focusFallback: link.matches("[data-guru-search-active-filter], [data-guru-search-clear-filters]") ? "filter" : "",
      });
      return;
    }

    const retry = target.closest("[data-guru-search-retry]");
    if (retry) {
      event.preventDefault();
      const failed = failedNavigations.get(root);
      if (failed) replaceSearch(root, failed.url, failed.options);
    }
  });

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = rootFor(target);
    if (!root) return;

    if (target.matches("[data-guru-search-sort]")) {
      let url;
      try {
        url = canonicalSearchUrl(root, window.location.href);
      } catch (_error) {
        url = boundedProductFallbackUrl(root, window.location.href);
      }
      if (target.value) url.searchParams.set("sort_by", target.value);
      else url.searchParams.delete("sort_by");
      url.searchParams.delete("page");
      syncSortValue(root);
      replaceSearch(root, url, {
        historyMode: "push",
        focusTarget: focusDescriptor(target),
        focusFallback: "sort",
        keepDesktopFilters: dialogFor(root)?.open,
      });
      return;
    }

    if (target instanceof HTMLInputElement && target.matches("[data-guru-panel-quick-control]")) {
      const type = target.dataset.guruPanelQuickControl;
      root.querySelectorAll(`[data-guru-${type}-input]`).forEach((input) => {
        if (!input.disabled || input.checked) input.checked = target.checked;
      });
      syncQuickFilters(root);
      queueDraftPreview(root);
      return;
    }

    if (target.closest("[data-guru-search-filter-form]")) {
      syncQuickFilters(root);
      if (desktopQuery.matches) {
        const form = formFor(root);
        const destination = validatedFilterUrl(root, form, { report: true });
        if (!destination) {
          beginIntent(root);
          markDraftInvalid(root);
          return;
        }
        replaceSearch(root, destination, {
          historyMode: "push",
          keepDesktopFilters: true,
          focusTarget: focusDescriptor(target),
          focusFallback: "filter",
        });
      } else {
        queueDraftPreview(root);
      }
    }
  });

  document.addEventListener("input", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const root = input.closest(rootSelector);
    if (!root) return;
    if (input.matches("[data-guru-search-input]")) {
      queueQuery(root, input);
    } else if (input.closest("[data-guru-search-filter-form]") && !desktopQuery.matches) {
      syncQuickFilters(root);
      queueDraftPreview(root);
    }
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const root = rootFor(form);
    if (!root) return;

    if (form.matches("[data-guru-search-query-form]")) {
      event.preventDefault();
      const input = form.querySelector("[data-guru-search-input]");
      if (input) {
        replaceSearch(root, urlFromQuery(root, input.value), {
          historyMode: "push",
          focusTarget: { kind: "query" },
          focusFallback: "query",
        });
      }
      return;
    }

    if (form.matches("[data-guru-search-sale-remove-form]")) {
      event.preventDefault();
      const destination = validatedFilterUrl(root, form, { report: true });
      if (!destination) {
        beginIntent(root);
        setNavigationError(root, localizedCopy(root, "routeError"), false);
        return;
      }
      replaceSearch(root, destination, {
        historyMode: "push",
        focusFallback: "filter",
      });
      return;
    }

    if (!form.matches("[data-guru-search-filter-form]")) return;
    event.preventDefault();
    const destination = validatedFilterUrl(root, form, { report: true });
    if (!destination) {
      beginIntent(root);
      markDraftInvalid(root);
      return;
    }
    const apply = applyFor(root);
    const evidence = validatedPreviews.get(form);
    if (
      apply?.disabled ||
      !evidence ||
      evidence.generation !== (previewGenerations.get(form) || 0) ||
      evidence.url !== relativeUrl(destination) ||
      evidence.total <= 0
    ) {
      queueDraftPreview(root);
      return;
    }
    closeFilters(root, { restore: false, discardDraft: false });
    replaceSearch(root, destination, { historyMode: "push", focusFallback: "filter" });
  });

  document.addEventListener("cancel", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches("[data-guru-search-filter-dialog]")) return;
    event.preventDefault();
    closeFilters(rootFor(dialog));
  }, true);

  document.addEventListener("click", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches("[data-guru-search-filter-dialog]")) return;
    if (event.target !== dialog) return;
    const root = rootFor(dialog);
    if (desktopQuery.matches && root?.dataset.guruSearchFilterLayout === "sidebar") return;
    closeFilters(root);
  });

  desktopQuery.addEventListener("change", () => {
    document.querySelectorAll(rootSelector).forEach((root) => closeFilters(root, { restore: false }));
  });

  window.addEventListener("popstate", () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    let destination;
    try {
      destination = canonicalSearchUrl(root, window.location.href);
    } catch (_error) {
      destination = boundedProductFallbackUrl(root, window.location.href);
    }
    const current = new URL(window.location.href);
    replaceSearch(root, destination, {
      historyMode: relativeUrl(destination) === `${current.pathname}${current.search}` ? "none" : "replace",
    });
  });

  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll?.("noscript").forEach((node) => node.remove());
    const root = event.target.querySelector?.(rootSelector);
    if (root) initializeRoot(root);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.querySelector?.(rootSelector);
    if (!root) return;
    const navigationRequest = navigationRequests.get(root);
    cleanupInfiniteLoading(root);
    navigationRequest?.abort();
    releaseLoading(navigationRequest);
    navigationRequests.delete(root);
    suggestionRequests.get(root)?.abort();
    window.clearTimeout(queryTimers.get(root));
    cancelPreview(root);
    document.body.classList.remove("guru-search-filter-open");
    initializedRoots.delete(root);
  });

  const initialize = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initializeRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  } else {
    initialize();
  }

  window[namespace] = { initialize, navigate: replaceSearch };
})();
